import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../../services/api/authApi";
import { showError, showSuccess } from "../../lib/toast";
import { useAuth } from "../../contexts/AuthContext";

interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordFormErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const ChangePassword: React.FC = () => {
  const [formData, setFormData] = useState<ChangePasswordFormData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ChangePasswordFormErrors>({});
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();

  // Check authentication status
  useEffect(() => {
    if (!isLoggedIn || !user) {
      showError("Please log in to change your password");
      navigate("/login");
      return;
    }
  }, [isLoggedIn, user, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof ChangePasswordFormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ChangePasswordFormErrors = {};
    let isValid = true;

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
      isValid = false;
    }

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
      isValid = false;
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "New password must be at least 6 characters";
      isValid = false;
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = "New password must be different from current password";
      isValid = false;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
      isValid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "New Passwords and Confirm Passwords do not match";
      isValid = false;
    }


    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await authApi.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      showSuccess("Password changed successfully");
      navigate("/editprofile");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to change password. Please try again.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex flex-column">
      {/* Breadcrumb */}
      <div className="py-3 font_two">
        <div className="container">
          <p className="text-muted small mb-0 font_two">
            <a href="/" className="text-muted text-decoration-none">
              Home
            </a>{" "}
            /<span className="fw-bold"> Change Password</span>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container flex-grow-1 d-flex flex-column flex-md-row py-4">
        {/* Left Content */}
        <div className="col-md-6 mb-4">
          <h4 className="display-6 mb-3">Change Password</h4>
          <p className="text-muted font_two mb-3">
            Update your password to keep your account secure.
          </p>
          <p className="text-muted small">
            <strong>Security Tips:</strong>
          </p>
          <ul className="text-muted small">
            <li>Use a strong password with at least 6 characters</li>
            <li>Include a mix of letters, numbers, and symbols</li>
            <li>Don't reuse passwords from other accounts</li>
            <li>Never share your password with anyone</li>
          </ul>
        </div>

        {/* Right Form */}
        <div className="col-md-6">
          <form
            onSubmit={handleSubmit}
            className="bg-white p-4 rounded-3 shadow-sm"
          >
            <div className="mb-4">
              <label htmlFor="currentPassword" className="form-label fw-medium">
                Current Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  id="currentPassword"
                  name="currentPassword"
                  placeholder="Enter your current password"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                  className={`form-control form-control-sm custom-input ${errors.currentPassword ? "is-invalid" : ""}`}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
              {errors.currentPassword && (
                <div className="invalid-feedback">{errors.currentPassword}</div>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="newPassword" className="form-label fw-medium">
                New Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                  className={`form-control form-control-sm custom-input ${errors.newPassword ? "is-invalid" : ""}`}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
              {errors.newPassword && (
                <div className="invalid-feedback">{errors.newPassword}</div>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="confirmPassword" className="form-label fw-medium">
                Confirm New Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className={`form-control form-control-sm custom-input ${errors.confirmPassword ? "is-invalid" : ""}`}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
              {errors.confirmPassword && (
                <div className="invalid-feedback">{errors.confirmPassword}</div>
              )}
            </div>

            {Object.values(errors).length > 0 && (
              <div style={{color:"red", fontSize: 16, marginBottom:"10px"}}>
                {Object.values(errors)[0]}
              </div>
            )}

            <div className="text-center pt-2">
              <button
                type="submit"
                className="btn btn-dark primary-btn w-50 rounded-pill"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating Password...
                  </>
                ) : (
                  "Update Password"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
